import { useState, useCallback, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout';
import { Button } from '@/components/ui';
import { useAuth } from '@/features/auth';
import { useEffectiveBusinessId } from '@/hooks';
import { useBusinessContextStore } from '@/features/business-context';
import { ProjectCustomersManager } from '../components/ProjectCustomersManager';
import { ProjectSection } from '../components/ProjectSection';
import { ProjectSiteSection } from '../components/ProjectSiteSection';
import { useCustomers } from '../hooks/useCustomers';
import { useCreateProject } from '../hooks/useCreateProject';
import {
  DEFAULT_PROJECT_FORM_DATA,
  type ProjectCreateFormData,
  type ProjectCustomerFormItem,
} from '../types';

// Email validation regex
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Canadian postal code regex
const POSTAL_CODE_REGEX = /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/;

export function ProjectCreatePage() {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const effectiveBusinessId = useEffectiveBusinessId();
  const selectedBusinessName = useBusinessContextStore(
    (state) => state.selectedBusinessName
  );

  const isSuperAdmin = profile?.role === 'super_admin';

  // Form state
  const [formData, setFormData] = useState<ProjectCreateFormData>(
    DEFAULT_PROJECT_FORM_DATA
  );
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Hooks
  const {
    customers,
    isLoading: isLoadingCustomers,
    isSearching: isSearchingCustomers,
    search: searchCustomers,
    clearSearch: clearCustomerSearch,
  } = useCustomers({
    businessId: effectiveBusinessId,
    autoFetch: true,
  });

  const {
    isSubmitting,
    error: submitError,
    submitProject,
    clearError,
  } = useCreateProject();

  // Handle customers change
  const handleCustomersChange = useCallback(
    (newCustomers: ProjectCustomerFormItem[]) => {
      setFormData((prev) => ({ ...prev, customers: newCustomers }));
      // Clear customer-related errors
      setErrors((prev) => {
        const newErrors = { ...prev };
        Object.keys(newErrors).forEach((key) => {
          if (key.startsWith('customer_') || key === 'customers') {
            delete newErrors[key];
          }
        });
        return newErrors;
      });
      clearError();
    },
    [clearError]
  );

  // Handle project field changes
  const handleChange = useCallback(
    (field: string, value: string | boolean) => {
      setFormData((prev) => ({ ...prev, [field]: value }));

      // Clear error for this field when user starts typing
      setErrors((prev) => {
        if (prev[field]) {
          const { [field]: _, ...rest } = prev;
          return rest;
        }
        return prev;
      });
      clearError();
    },
    [clearError]
  );

  // Get primary customer's address for site section
  const primaryCustomerAddress = useMemo(() => {
    const primaryCustomer = formData.customers.find((c) => c.isPrimary);
    if (!primaryCustomer) return null;

    if (primaryCustomer.mode === 'existing' && primaryCustomer.existingCustomerId) {
      // Find in available customers list
      const existingCustomer = customers.find(
        (c) => c.id === primaryCustomer.existingCustomerId
      );
      if (existingCustomer) {
        return {
          streetAddress: existingCustomer.billing_street_address || '',
          unit: existingCustomer.billing_unit || '',
          city: existingCustomer.billing_city || '',
          province: existingCustomer.billing_province || '',
          postalCode: existingCustomer.billing_postal_code || '',
          country: existingCustomer.billing_country || '',
        };
      }
    }

    // Use new customer form data
    return {
      streetAddress: primaryCustomer.billingStreetAddress,
      unit: primaryCustomer.billingUnit,
      city: primaryCustomer.billingCity,
      province: primaryCustomer.billingProvince,
      postalCode: primaryCustomer.billingPostalCode,
      country: primaryCustomer.billingCountry,
    };
  }, [formData.customers, customers]);

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Customers validation
    if (formData.customers.length === 0) {
      newErrors.customers = 'At least one customer is required';
    } else {
      // Check each customer
      formData.customers.forEach((customer, index) => {
        const prefix = `customer_${index}_`;

        if (customer.mode === 'new') {
          if (!customer.firstName.trim()) {
            newErrors[`${prefix}firstName`] = 'First name is required';
          }
          if (!customer.lastName.trim()) {
            newErrors[`${prefix}lastName`] = 'Last name is required';
          }
          if (!customer.email.trim()) {
            newErrors[`${prefix}email`] = 'Email is required';
          } else if (!EMAIL_REGEX.test(customer.email)) {
            newErrors[`${prefix}email`] = 'Please enter a valid email address';
          }
          if (!customer.billingStreetAddress.trim()) {
            newErrors[`${prefix}billingStreetAddress`] = 'Street address is required';
          }
          if (!customer.billingCity.trim()) {
            newErrors[`${prefix}billingCity`] = 'City is required';
          }
          if (!customer.billingProvince) {
            newErrors[`${prefix}billingProvince`] = 'Province is required';
          }
          if (!customer.billingPostalCode.trim()) {
            newErrors[`${prefix}billingPostalCode`] = 'Postal code is required';
          } else if (!POSTAL_CODE_REGEX.test(customer.billingPostalCode.trim())) {
            newErrors[`${prefix}billingPostalCode`] =
              'Please enter a valid postal code (e.g., M5V 1A1)';
          }
          if (!customer.billingCountry) {
            newErrors[`${prefix}billingCountry`] = 'Country is required';
          }
        } else {
          if (!customer.existingCustomerId) {
            newErrors[`${prefix}existingCustomerId`] = 'Please select a customer';
          }
        }
      });

      // Validate primary and billing
      const hasPrimary = formData.customers.some((c) => c.isPrimary);
      const hasBilling = formData.customers.some((c) => c.isBilling);

      if (!hasPrimary) {
        newErrors.customers = 'Please select a primary contact';
      } else if (!hasBilling) {
        newErrors.customers = 'Please select a billing contact';
      }
    }

    // Project validation
    if (!formData.projectName.trim()) {
      newErrors.projectName = 'Project name is required';
    }

    // Site location validation (only when not using customer address)
    if (!formData.useSameAsCustomerAddress) {
      if (!formData.siteStreetAddress.trim()) {
        newErrors.siteStreetAddress = 'Street address is required';
      }
      if (!formData.siteCity.trim()) {
        newErrors.siteCity = 'City is required';
      }
      if (!formData.siteProvince) {
        newErrors.siteProvince = 'Province is required';
      }
      if (!formData.sitePostalCode.trim()) {
        newErrors.sitePostalCode = 'Postal code is required';
      } else if (!POSTAL_CODE_REGEX.test(formData.sitePostalCode.trim())) {
        newErrors.sitePostalCode = 'Please enter a valid postal code (e.g., M5V 1A1)';
      }
      if (!formData.siteCountry) {
        newErrors.siteCountry = 'Country is required';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!effectiveBusinessId) {
      setErrors({ form: 'Please select a business to continue' });
      return;
    }

    if (!validateForm()) {
      return;
    }

    // Warn if creating customer with existing email (but allow it)
    // The user has chosen not to use the existing customer

    const newProject = await submitProject(formData, effectiveBusinessId);

    if (newProject) {
      navigate(`/projects/${newProject.id}`);
    }
  };

  // Check if business is selected (for super admins)
  const showBusinessWarning = isSuperAdmin && !effectiveBusinessId;

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto">
        {/* Page Header */}
        <div className="mb-6">
          <Link
            to="/projects"
            className="inline-flex items-center gap-1 text-sm text-secondary-500 
                       hover:text-secondary-700 transition-colors mb-2"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Back to Projects
          </Link>
          <h1 className="text-2xl font-bold text-secondary-900">
            Create New Project
          </h1>
          <p className="text-secondary-600 mt-1">
            Add a new project with customer information
          </p>
        </div>

        {/* Business Warning for Super Admins */}
        {showBusinessWarning && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-start gap-3">
              <svg
                className="w-5 h-5 text-amber-500 mt-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <div>
                <h3 className="font-medium text-amber-800">
                  No Business Selected
                </h3>
                <p className="text-sm text-amber-700 mt-1">
                  Please select a business from the dropdown in the navigation
                  bar before creating a project.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Business Context Info (for Super Admins) */}
        {isSuperAdmin && effectiveBusinessId && (
          <div className="mb-6 p-4 bg-primary-50 border border-primary-200 rounded-lg">
            <p className="text-sm text-primary-700">
              <span className="font-medium">Creating project for:</span>{' '}
              {selectedBusinessName}
            </p>
          </div>
        )}

        {/* Error Alert */}
        {(submitError || errors.form) && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start gap-3">
              <svg
                className="w-5 h-5 text-red-500 mt-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div>
                <h3 className="font-medium text-red-800">Error</h3>
                <p className="text-sm text-red-700 mt-1">
                  {submitError || errors.form}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Customers Section */}
          <ProjectCustomersManager
            customers={formData.customers}
            availableCustomers={customers}
            onChange={handleCustomersChange}
            onCustomerSearch={searchCustomers}
            onClearCustomerSearch={clearCustomerSearch}
            isLoadingCustomers={isLoadingCustomers}
            isSearchingCustomers={isSearchingCustomers}
            disabled={isSubmitting || showBusinessWarning}
            errors={errors}
          />

          {/* Site Location Section */}
          <ProjectSiteSection
            useSameAsCustomerAddress={formData.useSameAsCustomerAddress}
            siteStreetAddress={formData.siteStreetAddress}
            siteUnit={formData.siteUnit}
            siteCity={formData.siteCity}
            siteProvince={formData.siteProvince}
            sitePostalCode={formData.sitePostalCode}
            siteCountry={formData.siteCountry}
            primaryCustomerAddress={primaryCustomerAddress}
            onChange={handleChange}
            errors={errors}
            disabled={isSubmitting || showBusinessWarning}
          />

          {/* Project Section */}
          <ProjectSection
            formData={formData}
            onChange={handleChange}
            errors={errors}
            disabled={isSubmitting || showBusinessWarning}
          />

          {/* Submit Buttons */}
          <div className="flex flex-col-reverse sm:flex-row gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/projects')}
              disabled={isSubmitting}
              className="flex-1 sm:flex-none"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              isLoading={isSubmitting}
              disabled={isSubmitting || showBusinessWarning}
              className="flex-1 sm:flex-none"
            >
              Create Project
            </Button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
}